import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import axios from 'axios';

function DNSLookup() {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleLookup = async () => {
    if (!domain) {
      setError('Please enter a domain name');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Use DNS over HTTPS (DoH) API
      const response = await axios.get(`https://dns.google/resolve?name=${domain}&type=A`);
      const addresses = response.data.Answer ? response.data.Answer.map(answer => answer.data) : [];
      
      if (addresses.length === 0) {
        setError('No A records found for this domain');
        return;
      }
      
      setResult({
        domain: domain,
        addresses: addresses
      });
    } catch (err) {
      setError('Failed to resolve domain. Please check the domain name.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        DNS Lookup
      </Typography>
      <Card sx={{ 
        p: 0,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Domain Name"
              variant="outlined"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleLookup}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Lookup'}
            </Button>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {result && result.addresses && result.addresses.length > 0 && (
              <Card sx={{ 
                mt: 2, 
                p: 3, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: 2
              }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  DNS Results
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="body1">
                    <strong>Domain:</strong> {result.domain}
                  </Typography>
                  <Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>IP Addresses:</strong>
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      {result.addresses.map((ip, index) => (
                        <Typography 
                          key={index} 
                          variant="body2" 
                          sx={{ 
                            mb: 0.5,
                            fontFamily: 'monospace',
                            color: 'success.main',
                            fontWeight: 500
                          }}
                        >
                          {ip}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Card>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default DNSLookup;
